import type { SyncEngine } from './engine.ts';
import type { RepwiseStore } from './store.ts';

/**
 * Drain triggers (ADR-0003, ADR-0022). iOS web apps have no Background Sync, so these are the only chances:
 * - a local write (debounced), the app going to the background, and a 30 s timer while visible: `auto`, which
 *   respects the backoff and the open circuit breaker (no hammering while underground);
 * - the device coming back online: `online`, and the app opening: `start`, which may always probe;
 * - a wake-up timer at the engine's next allowed attempt, so a backoff or a 30-minute pause ends on time.
 */
export function startAutoSync(engine: SyncEngine, store: RepwiseStore, win: Window & typeof globalThis = window, intervalMs = 30_000): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let wake: ReturnType<typeof setTimeout> | undefined;
  const kick = (delay = 0) => {
    clearTimeout(timer);
    timer = setTimeout(() => void engine.drain('auto').then(arm), delay);
  };
  /** Wake exactly when the next automatic attempt is allowed (after a backoff or the 30-minute pause). */
  const arm = () => {
    clearTimeout(wake);
    const at = engine.nextAttemptAt();
    const wait = at - Date.now();
    if (at > 0 && wait > 0) wake = setTimeout(() => void engine.drain('auto').then(arm), Math.min(wait + 50, 2 ** 31 - 1));
  };
  const offChange = store.onChange(() => kick(400)); // debounce bursts of logs
  const offStatus = engine.onStatus(arm);
  const onVisibility = () => {
    if (win.document.visibilityState === 'hidden') void engine.drain('auto');
    else kick();
  };
  const onOnline = () => {
    clearTimeout(timer);
    void engine.retryNow('online').then(arm); // connectivity came back: probe now, whatever the breaker says
  };
  win.document.addEventListener('visibilitychange', onVisibility);
  win.addEventListener('online', onOnline);
  const interval = setInterval(() => {
    if (win.document.visibilityState === 'visible') void engine.drain('auto');
  }, intervalMs);
  void engine.drain('start').then(arm);
  return () => {
    offChange();
    offStatus();
    clearTimeout(timer);
    clearTimeout(wake);
    clearInterval(interval);
    win.document.removeEventListener('visibilitychange', onVisibility);
    win.removeEventListener('online', onOnline);
  };
}
